import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  IconButton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  tabsClasses,
  useTheme,
} from "@mui/material";
import { FieldArray, useFormik } from "formik";
import {
  useCreateRestaurantMutation,
  useGetRestaurantQuery,
  useDeleteRestaurantScheduleMutation,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useStyles } from "../../../theme/appStyles";
import CloseIcon from "@mui/icons-material/Close";
import {
  formatPhoneNumberForDisplay,
  validateEmail,
  validatePhoneNumber,
} from "../../../Components/Validation/validationUtils";
import { useNavigate, useParams } from "react-router-dom";
import { IRestaurant } from "../../../Api/Interface/api.interface";
import { FaTrash } from "react-icons/fa";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

enum Section {
  General = "General",
  TimeSlot = "TimeSlot",
}

interface TimeSlot {
  id: number;
  startTime: string;
  endTime: string;
}

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id: restId } = useParams<{ id: string }>();
  const [newDepartment, { isLoading }] = useCreateRestaurantMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  const [activeSection, setActiveSection] = useState<Section>(Section.General);
  const handleContainerChange = (section: Section) => {
    setActiveSection(section);
  };
  const [slotToDelete, setSlotToDelete] = useState<{
    index: number;
    slotIndex: number;
  } | null>(null);

  const {
    data: restData,
    isLoading: restIsLoading,
    isError: restIsError,
    refetch,
  } = useGetRestaurantQuery({
    searchText: "",
    id: String(restId),
  });

  const initialRestData = useMemo(() => {
    if (restData && Array.isArray(restData.data) && restData.data.length > 0) {
      return restData.data[0] as IRestaurant;
    }
    return null;
  }, [restData]);

  const handlePhoneChange = (
    fieldName: string,
    value: string,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const formattedValue = formatPhoneNumberForDisplay(value);
    setFieldValue(fieldName, formattedValue);
  };

  const formik = useFormik({
    initialValues: {
      id: initialRestData?.id || "",
      branchsName: initialRestData?.branchsName || "",
      branchsDescription: initialRestData?.branchsDescription || "",
      branchsStreetAddress: initialRestData?.branchsStreetAddress || "",
      branchsStreetAddress2:
        initialRestData?.branchsStreetAddress2 || "",
      branchsCity: initialRestData?.branchsCity || "",
      branchsProvince: initialRestData?.branchsProvince || "",
      branchsPostalCode: initialRestData?.branchsPostalCode || "",
      branchsWebsite: initialRestData?.branchsWebsite || "",
      branchsTakeout: initialRestData?.branchsTakeout || "",
      branchsDelivery: initialRestData?.branchsDelivery || "",
      branchsDeliveryFee: initialRestData?.branchsDeliveryFee || "",
      branchsMiniDeliveryAmount:
        initialRestData?.branchsMiniDeliveryAmount || "",
      branchsFreeDeliveryAmount:
        initialRestData?.branchsFreeDeliveryAmount || "",
      branchsDeliveryRadius:
        initialRestData?.branchsDeliveryRadius || "",
      branchsPhoneNumber: initialRestData?.branchsPhoneNumber
        ? formatPhoneNumberForDisplay(initialRestData.branchsPhoneNumber)
        : "",
      branchsAltPhoneNumber: initialRestData?.branchsAltPhoneNumber
        ? formatPhoneNumberForDisplay(initialRestData.branchsAltPhoneNumber)
        : "",
      branchsEmail: initialRestData?.branchsEmail || "",
      availability: initialRestData?.availability || [
        {
          day: "Sunday",
          timeSlots: [],
        },
        {
          day: "Monday",
          timeSlots: [],
        },
        {
          day: "Tuesday",
          timeSlots: [],
        },
        {
          day: "Wednesday",
          timeSlots: [],
        },
        {
          day: "Thursday",
          timeSlots: [],
        },
        {
          day: "Friday",
          timeSlots: [],
        },
        {
          day: "Saturday",
          timeSlots: [],
        },
      ],
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: { [key: string]: string } = {};

      const emailError = validateEmail(values.branchsEmail);
      if (emailError) {
        errors.branchsEmail = emailError;
      }

      const mobileNumberError = validatePhoneNumber(
        values.branchsPhoneNumber
      );
      if (mobileNumberError) {
        errors.branchsPhoneNumber = mobileNumberError;
      }

      const altMobileNumberError = validatePhoneNumber(
        values.branchsAltPhoneNumber
      );
      if (altMobileNumberError) {
        errors.branchsAltPhoneNumber = altMobileNumberError;
      }

      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const temData = {
          id: values.id,
          branchsName: values.branchsName,
          branchsDescription: values.branchsDescription,
          branchsStreetAddress: values.branchsStreetAddress,
          branchsStreetAddress2: values.branchsStreetAddress2,
          branchsCity: values.branchsCity,
          branchsProvince: values.branchsProvince,
          branchsPostalCode: values.branchsPostalCode,
          branchsWebsite: values.branchsWebsite,
          branchsTakeout: values.branchsTakeout,
          branchsDelivery: values.branchsDelivery,
          branchsDeliveryFee: values.branchsDeliveryFee,
          branchsMiniDeliveryAmount: values.branchsMiniDeliveryAmount,
          branchsFreeDeliveryAmount: values.branchsFreeDeliveryAmount,
          branchsDeliveryRadius: values.branchsDeliveryRadius,
          branchsPhoneNumber: values.branchsPhoneNumber.replace(
            /-/g,
            ""
          ),
          branchsAltPhoneNumber: values.branchsAltPhoneNumber.replace(
            /-/g,
            ""
          ),
          branchsEmail: values.branchsEmail?.trim().toLowerCase(),
          availability: values.availability,
        };
        const addCompanyResponse = await newDepartment(temData).unwrap();
        if (!addCompanyResponse.status) {
          showErrorMessage(addCompanyResponse.message);
        } else {
          showMessage(addCompanyResponse.message);
          resetForm();
          navigate("/branch/list");
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formValid = useMemo(() => {
    return (
      formik.values.branchsName !== "" &&
      formik.values.branchsName !== undefined
    );
  }, [formik]);

  const handleDeleteClick = (index: number, slotIndex: number) => {
    setSlotToDelete({ index, slotIndex });
  };

  const handleDeleteCancel = () => {
    setSlotToDelete(null);
  };
  const [deleteCategory] = useDeleteRestaurantScheduleMutation();
  const handleDeleteConfirm = async () => {
    if (slotToDelete) {
      const { index, slotIndex } = slotToDelete;
      const slotId = formik.values.availability[index].timeSlots[slotIndex].id;

      try {
        await deleteCategory(String(slotId));

        const timeSlots = formik.values.availability[index]
          .timeSlots as TimeSlot[];

        formik.setFieldValue(
          `availability.${index}.timeSlots`,
          timeSlots.filter((slot, i) => i !== slotIndex)
        );
      } catch (error) {
        console.error("Error deleting time slot:", error);
      }
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case Section.TimeSlot:
        return (
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              {formik.values.availability.map((day, index) => (
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <Card className={classes.card}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ padding: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                      >
                        <Typography className={classes.cardTitle}>
                          {day.day} Availability
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid container spacing={3} p={"24px"}>
                      <Grid item lg={12} md={12} sm={12} xs={12} key={day.day}>
                        <Grid container spacing={2} alignItems="center">
                          <FieldArray name={`availability.${index}.timeSlots`}>
                            {({ push, remove }) => (
                              <>
                                <Grid item lg={12} md={12} sm={12} xs={12}>
                                  <Grid
                                    container
                                    direction="row"
                                    alignItems="center"
                                    spacing={2}
                                  >
                                    <TableContainer>
                                      <Table>
                                        <TableHead>
                                          <TableRow>
                                            <TableCell
                                              className={
                                                classes.tablheaderCreate
                                              }
                                            >
                                              Start Time
                                            </TableCell>
                                            <TableCell
                                              className={
                                                classes.tablheaderCreate
                                              }
                                            >
                                              End Time
                                            </TableCell>

                                            <TableCell
                                              className={
                                                classes.tablheaderCreate
                                              }
                                              sx={{ textAlign: "right" }}
                                            >
                                              Action
                                            </TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {formik.values.availability[
                                            index
                                          ].timeSlots.map((slot, slotIndex) => (
                                            <TableRow key={slotIndex}>
                                              <TableCell>
                                                <TextField
                                                  placeholder="Start Time"
                                                  size="small"
                                                  type="time"
                                                  {...formik.getFieldProps(
                                                    `availability.${index}.timeSlots.${slotIndex}.startTime`
                                                  )}
                                                  className={classes.textField}
                                                  InputProps={{
                                                    sx: {
                                                      fontSize: 14,
                                                    },
                                                  }}
                                                  InputLabelProps={{
                                                    sx: {
                                                      fontSize: 14,
                                                    },
                                                  }}
                                                />
                                              </TableCell>
                                              <TableCell>
                                                <TextField
                                                  placeholder="End Time"
                                                  size="small"
                                                  type="time"
                                                  {...formik.getFieldProps(
                                                    `availability.${index}.timeSlots.${slotIndex}.endTime`
                                                  )}
                                                  className={classes.textField}
                                                  InputProps={{
                                                    sx: {
                                                      fontSize: 14,
                                                    },
                                                  }}
                                                  InputLabelProps={{
                                                    sx: {
                                                      fontSize: 14,
                                                    },
                                                  }}
                                                />
                                              </TableCell>

                                              <TableCell>
                                                <Grid
                                                  container
                                                  spacing={1}
                                                  sx={{
                                                    display: "flex",
                                                    justifyContent: "flex-end",
                                                  }}
                                                >
                                                  <Grid item>
                                                    <IconButton
                                                      color="error"
                                                      onClick={() =>
                                                        handleDeleteClick(
                                                          index,
                                                          slotIndex
                                                        )
                                                      }
                                                      // disabled={
                                                      //   formik.values
                                                      //     .availability[
                                                      //     index
                                                      //   ].timeSlots
                                                      //     .length === 1
                                                      // }
                                                    >
                                                      <FaTrash />
                                                    </IconButton>
                                                  </Grid>
                                                </Grid>
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  </Grid>
                                </Grid>

                                <Grid
                                  item
                                  lg={12}
                                  md={12}
                                  sm={12}
                                  xs={12}
                                  display="flex"
                                  sx={{
                                    justifyContent: "flex-end",
                                    alignItems: "flex-end",
                                    textAlign: "end",
                                  }}
                                >
                                  <Button
                                    variant="contained"
                                    // startIcon={<AddIcon />}
                                    className={classes.addButton}
                                    onClick={() =>
                                      push({
                                        id: null,
                                        startTime: "",
                                        endTime: "",
                                      })
                                    }
                                  >
                                    <AddIcon />
                                  </Button>
                                </Grid>
                              </>
                            )}
                          </FieldArray>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </form>
        );
      case Section.General:
        return (
          <div>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={2}>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">
                        Name <span className={classes.requiedfield}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item lg={6} md={9} sm={12} xs={12}>
                      <TextField
                        placeholder="Restaurant Name"
                        size="small"
                        {...formik.getFieldProps("branchsName")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">Phone Number</Typography>
                    </Grid>
                    <Grid item lg={3} md={4.5} sm={12} xs={12}>
                      <TextField
                        placeholder="Phone Number"
                        size="small"
                        value={formik.values.branchsPhoneNumber}
                        onChange={(e) =>
                          handlePhoneChange(
                            "branchsPhoneNumber",
                            e.target.value,
                            formik.setFieldValue
                          )
                        }
                        fullWidth
                        onBlur={formik.handleBlur}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        error={
                          formik.touched.branchsPhoneNumber &&
                          Boolean(formik.errors.branchsPhoneNumber)
                        }
                        helperText={
                          formik.touched.branchsPhoneNumber &&
                          formik.errors.branchsPhoneNumber
                        }
                      />
                    </Grid>
                    <Grid item lg={3} md={4.5} sm={12} xs={12}>
                      <TextField
                        placeholder="Alt Phone Number"
                        size="small"
                        value={formik.values.branchsAltPhoneNumber}
                        onChange={(e) =>
                          handlePhoneChange(
                            "branchsAltPhoneNumber",
                            e.target.value,
                            formik.setFieldValue
                          )
                        }
                        onBlur={formik.handleBlur}
                        className={classes.textField}
                        fullWidth
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        error={
                          formik.touched.branchsAltPhoneNumber &&
                          Boolean(formik.errors.branchsAltPhoneNumber)
                        }
                        helperText={
                          formik.touched.branchsAltPhoneNumber &&
                          formik.errors.branchsAltPhoneNumber
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">E-mail</Typography>
                    </Grid>
                    <Grid item lg={3} md={9} sm={12} xs={12}>
                      <TextField
                        placeholder="E-mail"
                        size="small"
                        type="email"
                        {...formik.getFieldProps("branchsEmail")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        error={
                          formik.touched.branchsEmail &&
                          Boolean(formik.errors.branchsEmail)
                        }
                        helperText={
                          formik.touched.branchsEmail &&
                          formik.errors.branchsEmail
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">
                        Street Address
                      </Typography>
                    </Grid>
                    <Grid item lg={6} md={9} sm={12} xs={12}>
                      <TextField
                        placeholder="Street Address"
                        size="small"
                        {...formik.getFieldProps("branchsStreetAddress")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">
                        Alt Street Address
                      </Typography>
                    </Grid>
                    <Grid item lg={6} md={9} sm={12} xs={12}>
                      <TextField
                        placeholder="Alt Street Address"
                        size="small"
                        {...formik.getFieldProps("branchsStreetAddress2")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">Address</Typography>
                    </Grid>
                    <Grid item lg={2} md={3} sm={12} xs={12}>
                      <TextField
                        placeholder="City"
                        size="small"
                        {...formik.getFieldProps("branchsCity")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item lg={2} md={3} sm={12} xs={12}>
                      <TextField
                        placeholder="Province"
                        size="small"
                        {...formik.getFieldProps("branchsProvince")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item lg={2} md={3} sm={12} xs={12}>
                      <TextField
                        placeholder="PostalCode"
                        size="small"
                        {...formik.getFieldProps("branchsPostalCode")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">Website</Typography>
                    </Grid>
                    <Grid item lg={6} md={9} sm={12} xs={12}>
                      <TextField
                        placeholder="Website"
                        size="small"
                        {...formik.getFieldProps("branchsWebsite")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">Takeout</Typography>
                    </Grid>
                    <Grid item lg={6} md={9} sm={12} xs={12}>
                      <TextField
                        placeholder="Takeout"
                        size="small"
                        {...formik.getFieldProps("branchsTakeout")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">Delivery</Typography>
                    </Grid>
                    <Grid item lg={6} md={9} sm={12} xs={12}>
                      <TextField
                        placeholder="Delivery"
                        size="small"
                        {...formik.getFieldProps("branchsDelivery")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">Delivery Fee</Typography>
                    </Grid>
                    <Grid item lg={3} md={9} sm={12} xs={12}>
                      <TextField
                        placeholder="Delivery Fee"
                        size="small"
                        type="number"
                        {...formik.getFieldProps("branchsDeliveryFee")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">
                        Mini Delivery Amount
                      </Typography>
                    </Grid>
                    <Grid item lg={3} md={9} sm={12} xs={12}>
                      <TextField
                        placeholder="Mini Delivery Amount"
                        size="small"
                        type="number"
                        {...formik.getFieldProps(
                          "branchsMiniDeliveryAmount"
                        )}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">
                        Free Delivery Amount
                      </Typography>
                    </Grid>
                    <Grid item lg={3} md={9} sm={12} xs={12}>
                      <TextField
                        placeholder="Free Delivery Amount"
                        size="small"
                        type="number"
                        {...formik.getFieldProps(
                          "branchsFreeDeliveryAmount"
                        )}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">Description</Typography>
                    </Grid>
                    <Grid item lg={6} md={9} sm={12} xs={12}>
                      <TextField
                        placeholder="Description"
                        size="small"
                        multiline
                        rows={8}
                        {...formik.getFieldProps("branchsDescription")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                  <Card className={classes.card}>
                    <Grid container spacing={3} p={"24px"}>
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        sx={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<CloseIcon />}
                          className={classes.addButton}
                          onClick={() => formik.resetForm()}
                        >
                          Reset
                        </Button>
                        <Box m={0.5}></Box>

                        <Button
                          variant="contained"
                          startIcon={
                            isLoading ? (
                              <CircularProgress
                                size={18}
                                className={classes.isLoading}
                              />
                            ) : (
                              <CheckCircleOutlineIcon />
                            )
                          }
                          className={classes.addButton}
                          onClick={() => formik.handleSubmit()}
                          disabled={!formValid || isLoading}
                        >
                          Save
                        </Button>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              </Grid>
            </form>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Grid container spacing={2}>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Box>
                      <Tabs
                        variant="scrollable"
                        value={activeSection}
                        onChange={(_, newValue) =>
                          handleContainerChange(newValue)
                        }
                        aria-label="scrollable auto tabs example"
                        sx={{
                          [`& .${tabsClasses.scrollButtons}`]: {
                            "&.Mui-disabled": { opacity: 0.3 },
                          },
                          "& .MuiTabs-scrollButtons svg": {
                            fontSize: 40,
                            color: "black",
                          },
                          borderBottom: "1px solid #ccc",
                          px: 2,
                          pt: 3,
                        }}
                      >
                        <Tab
                          className={classes.cardTitleTab}
                          value={Section.General}
                          label="General"
                        />
                        <Tab
                          className={classes.cardTitleTab}
                          value={Section.TimeSlot}
                          label="TimeSlot"
                        />
                      </Tabs>
                    </Box>
                  </Grid>
                </Grid>

                <Grid container spacing={3} p={"24px"}>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    {renderContent()}
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default Index;
